// Table Config Imports
import {
    table_config_compact_38,
    table_config_search_across,
    table_config_compact_somatic,
} from "../../../../../../../../../config-files/table-config/snv-table/table_config";

// React and Material UI Imports
import React from "react";
import { TableCell, TableSortLabel } from "@mui/material";

// Custom Functions and Components
import { colorCodeClinVar, formatEffect, getPredictorValue, getSlidePopulation, colorOncoEvidences } from "./cell_renderers";
import GPAP_Tooltip from "../../../../../../../../../gpap-lib/components/GPAP_Tooltip";
import GPAP_InfoIcon from "../../../../../../../../../gpap-lib/components/GPAP_InfoIcon";
import MappedText from "../../../../../../../../instand/reports/create-report/shared-components/mapText";
import getTranscriptLink from "./gpap_table_row/getTranscriptLink";

// CSS Import
import "./row-style.css";
import LabelIcon from "@mui/icons-material/Label";
import FlagIcon from "@mui/icons-material/Flag";

const SIGENES_COLUMN_COLOR = "#8e44ad";



function getConfig(configType){

    let table_config;
    if(configType === "default")
    {
        table_config= table_config_compact_38;
    }
    else if(configType === "somatic"){
        table_config = table_config_compact_somatic;
    }
    else{
        table_config = table_config_search_across;
    }

    return table_config;

}


function getMainHeaders(configType, annotations){

    const table_config = getConfig(configType);

    // Filter out main headings that have no visible subheadings
    let visibleMainHeadings = table_config.filter(mainH => {
        let visibleSubheadings = mainH.subheadings.filter(s => window.config.field_filtering ? s.visible !== false : true);
        return visibleSubheadings.length > 0;
    });

    let headers_main= visibleMainHeadings.map(function(mainH,index){
        // Filter visible columns and calculate colSpan based on visible columns only
        let visibleSubheadings = mainH.subheadings.filter(s => window.config.field_filtering ? s.visible !== false : true);
        let colSpan = visibleSubheadings.length;

        if(!annotations && mainH.mainHeading && mainH.mainHeading === "Clinical association"){
            // TODO: create a configuration to organize this;
            // there are more options by default if hg38;
            
            //Check if it is somatic and assign colSpan accordingly
            const containsId = mainH.subheadings.find(obj => obj.id === "OCScore");

            if (containsId) {
                if (window.config.field_filtering){
                    colSpan = 4;
                }
                else{
                    colSpan = 6;
                }
            }
            else{
                // Search across/cohort has 4 clinical columns (OMIM, ACMG evidences, ACMG classification, ClinVar) — use natural colSpan
                if (window.config.field_filtering && configType !== "search_across"){
                    colSpan = 1;
                }
                else{
                    colSpan = 4;
                }
            }
        }


        return <TableCell
                            key={"tcell_" + index}
                            align="center"
                            colSpan={colSpan}
                            style={{
                              borderRight: "1px solid white",
                              color: "white",
                              background: "#4575B4"
                          }}
        >
          <MappedText text={mainH.mainHeading} />
        </TableCell>



    });

    let head_1 = <TableCell key={"tcell_main_expand"} style={{ background: "#4575B4" }} colSpan={1} />

    let headers = [<TableCell  key={"tcell_s" + 0} style={{ background: "#4575B4" }} align="center" colSpan={1}/>]

    const sigenesEnabled = Boolean(window.config && window.config.werfen_endpoint);
    const sigenesMainHeader = sigenesEnabled
        ? [
            <TableCell
                key={"tcell_main_sigenes"}
                style={{ background: "#4575B4" }}
                align="center"
                colSpan={1}
            />,
        ]
        : [];

    return [head_1, ...headers, ...sigenesMainHeader, ...headers_main];
}



function getContent (d) {

    let content = <MappedText text={d.label} />;


    if(d.id ==="acmg_verdict"){

        return <GPAP_Tooltip title={<MappedText text="Varsome ACMG classification"/>}>{content}</GPAP_Tooltip>
     }
    else if (d.id === "acmg_classifications"){
        return <GPAP_Tooltip title={<MappedText text="Varsome ACMG Criteria"/>}>{content}</GPAP_Tooltip>
    }

    else if (d.id === "mane_annotation"){
            content = <GPAP_Tooltip
            leaveDelay={1000}
            title={(
            <span><MappedText text="MANE Select is the transcript agreed as universal standard for clinical reporting, "/>
                    <a
                        href="https://www.ncbi.nlm.nih.gov/refseq/MANE/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'orange' }}
                    >
                     https://www.ncbi.nlm.nih.gov/refseq/MANE/
             </a>
           </span>
            )}
            interactive={true} // Set interactive to true to make the content within the tooltip clickable
        >
                {content}
        </GPAP_Tooltip>
    }


    else if (d.id === "omim"){
        content = <GPAP_Tooltip
        leaveDelay={1000}
        title={(<span>
+                <MappedText text="The annotation is done with periodic OMIM updates through an internal API built with the same rationale as the OMIM one," />{" "}
            <a
                href="https://www.omim.org/api"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'orange' }}
            >
                https://www.omim.org/api
            </a>
        </span>
        
        )}
        interactive={true} // Set interactive to true to make the content within the tooltip clickable
    >
            {content}
    </GPAP_Tooltip>
}

    else if (d.id === "intervar_evidence"){
        content = <GPAP_Tooltip
            //enterDelay={500}
            leaveDelay={1000}
            title={(
                <span><MappedText text="Clinical significance available for exonic SNVs and based on InterVar," />
                <a
                    href="https://wintervar.wglab.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'orange' }}
                >
                    https://wintervar.wglab.org/
                </a>
            </span>
            )}
            interactive={true} // Set interactive to true to make the content within the tooltip clickable
        >
            {content}
        </GPAP_Tooltip>
    }

    return content;

}



function getHeaders(orderBy, onRequestSort, createSortHandler, order, configType, annotations, headerClasses, obj){



    const table_config = getConfig(configType);

    // Filter out main headings that have no visible subheadings
    let visibleMainHeadings = table_config.filter(mainH => {
        let visibleSubheadings = mainH.subheadings.filter(s => window.config.field_filtering ? s.visible !== false : true);
        return visibleSubheadings.length > 0;
    });

    let headers = visibleMainHeadings.map(function(mainH){

        let subheadings = mainH.subheadings;

        if(!annotations){
            subheadings = subheadings.filter(s => !["acmg_verdict", "acmg_classifications"].includes(s.id))
        }

        // Filter out invisible columns (only when instand is true)
        subheadings = subheadings.filter(s => window.config.field_filtering ? s.visible !== false : true);

        return subheadings.map(function(d){

            let content = getContent(d);

            let tableCell = <TableCell
                key={d.id}
                align={"center"}
                padding={d.disablePadding ? 'none' : 'default'}
                sortDirection={orderBy === d.id ? order : false}>
                <TableSortLabel
                    active={orderBy === d.id}
                    direction={orderBy === d.id ? order : 'asc'}
                    onClick={createSortHandler(d.id, d.numeric)}>
                     <span> {content}</span>
                    {orderBy === d.id ? (
                        <span className={headerClasses.visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </span>
                    ) : null}
                </TableSortLabel>
            </TableCell>


            return (d.tooltip!== undefined)
                ? <GPAP_Tooltip title={<MappedText text={d.tooltip}/>}>{tableCell}</GPAP_Tooltip>
                : tableCell

        })


    })

    let cell_one = <TableCell key={"tcell_expand_sub"} colSpan={1} />

    let tableHeaders = [ cell_one,
        <TableCell
            key={"tcell_label_sub"}
            padding="checkbox">
            <LabelIcon color={"primary"}/>
        </TableCell>]

    if (window.config && window.config.werfen_endpoint) {
        tableHeaders.push(
            <TableCell
                key={"tcell_sigenes_sub"}
                padding="checkbox"
                align={"center"}>
                <GPAP_Tooltip title={<MappedText text="Sigenes - La biblioteca de variantes" />}>
                    <FlagIcon style={{ color: SIGENES_COLUMN_COLOR }} />
                </GPAP_Tooltip>
            </TableCell>
        );
    }

    return tableHeaders.concat(headers);

}



function getRow(row, labelId,flagged, configType, annotations, dataType){

    const table_config = getConfig(configType);

    const tooltip = (value, tip, val_check) => {

        if(value === val_check && tip!==undefined){
            return <GPAP_InfoIcon
                anchor={"true"}
                title={tip}/>
        }
        else if(value === val_check && tip ===undefined){
            return <GPAP_InfoIcon
                style={{
                    position: "relative",
                    top: "-30px",
                    left: "15px"
                }}
                title={"No Info available"}/>
        }


    };

    // Filter out main headings that have no visible subheadings
    let visibleMainHeadings = table_config.filter(mainH => {
        let visibleSubheadings = mainH.subheadings.filter(s => window.config.field_filtering ? s.visible !== false : true);
        return visibleSubheadings.length > 0;
    });

    return visibleMainHeadings.map(function(mainH){

        let subHeadings = mainH.subheadings;

        if(!annotations){
            subHeadings = subHeadings.filter(s => !["acmg_verdict", "acmg_classifications"].includes(s.id))
        }

        // Filter out invisible columns (only when instand is true)
        subHeadings = subHeadings.filter(s => window.config.field_filtering ? s.visible !== false : true);

        return subHeadings.map(function(d, index){

            let col_header=d.id;
            let value= row[col_header];
            if(col_header === "gene_name"){

                let final_value= (value === undefined ) ? "NA" : value;
                final_value = (typeof value === "object") ? JSON.stringify(final_value) : final_value;

                return    <TableCell component="th" id={labelId}
                                     key={col_header + "_" +  index}
                                     align="center"
                                     scope="row"
                                     padding="none">
                    <strong>{final_value}</strong>
                </TableCell>
            }
            else if(col_header === "Evidence"){
                return  <TableCell align="center" key={col_header + "_" + index}>{colorOncoEvidences(value)}</TableCell>
            }
            else if(col_header === "effect_impact"){
                return <TableCell align="center">{formatEffect(value)}</TableCell>

            }
            else if(col_header === "clinvar_clnsig"){
                return <TableCell align="center" key={col_header + "_" +  index}>
                    {colorCodeClinVar(value, "|", dataType)} {tooltip(value, row["clinvar_clnsigconf"], "C") }
                </TableCell>
            }
            else if(col_header === "intervar_automated"){
                return <TableCell align="center" key={col_header + "_" +  index}>
                    {colorCodeClinVar(value, ",")} {tooltip(value, row["intervar_automated"], "C") }
                </TableCell>
            }
            else if( (mainH.mainHeading === "Population frequency" && col_header !== "n_exp") || (mainH.mainHeading === "Population" && (col_header === "freqIntGermline" || col_header === "gnomad_af")) ){
                return <TableCell key={col_header + "_" +  index} align="center">{getSlidePopulation(value)}</TableCell>
            }
            else if( mainH.mainHeading === "Predictors" ){
                return <TableCell key={col_header + "_" +  index} align="center">{getPredictorValue(value, col_header)}</TableCell>
            }
            else if( col_header === "acmg_classifications" ){
                return <TableCell key={col_header + "_" +  index} align="center">{(value!== undefined) ? value.toString() : ""}</TableCell>
            }
           
            else if (col_header === "mane_annotation") {

                let hasManeSelectOrClinicalPlus = false;

                // Iterate over the transcripts_all array to check if mane_select or mane_plus_clinical exists
                if (row.transcripts_all) {
                    for (let i = 0; i < row.transcripts_all.length; i++) {
                        const transcript = row.transcripts_all[i];
                        if (transcript.mane_select || transcript.mane_plus_clinical) {
                            hasManeSelectOrClinicalPlus = true;
                            break;
                        }
                        
                    }
                }

                return <TableCell key={col_header + "_" + index} align="center"> {hasManeSelectOrClinicalPlus ? "Yes" : "No"} </TableCell>
            } else if (col_header === 'transcript_biotype') {
                

                let transcriptId = row.canonical_transcript ?? null;
                if (row.transcripts_all) {
                    if (row.transcripts_all.length === 1) {
                        transcriptId = row.transcripts_all[0].transcript_id;
                    } else {
                        const maneSelectTranscript = row.transcripts_all.find(t => t.mane_select);
                        if (maneSelectTranscript) {
                            transcriptId = maneSelectTranscript.transcript_id;
                        }
                    }
                }
                return (
                    <TableCell key={col_header + "_" + index} align="center">
                        <div>{(value !== undefined) ? value.toString() : ""}</div>
                        {transcriptId && <div>
                            <a
                                href={getTranscriptLink({ transcript_id: transcriptId }, row)}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'underline', color: '#3e699e' }}>{transcriptId}</a>
                        </div>}
                    </TableCell>
                );

            }
            else
            {

                let final_value= (value === undefined || value === "") ? "NA" : value;
                let cell_value = (typeof value === "object") ? JSON.stringify(final_value) : final_value;

                return <TableCell align="center" key={col_header + "_" +  index}>
                    {
                        (cell_value.length > 20)
                        ? <GPAP_Tooltip title={cell_value}>
                                <div className={"truncate"}>{cell_value}</div>
                            </GPAP_Tooltip>
                            : (cell_value === "NA") ?  <div style={{color: "#aaaaaa"}}>NA</div> : <GPAP_Tooltip title={cell_value}><div className={"truncate"}>{cell_value}</div></GPAP_Tooltip>
                    }

                </TableCell>
            }

        })

    });
}



export {getMainHeaders, getHeaders, getRow}